import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import api from "../api/axiosInstance";
import { subscribeToSessionChanges } from "../utils/sessionSync";

// Keeps this tab in step with the browser's login cookie.
//
// 1. Another tab logs in as a DIFFERENT user, or logs out -> this tab
//    reloads, so the page data AND the top nav both show the real user.
// 2. Safety net: when you come back to this tab, it quietly asks the server
//    "who am I?" and reloads if the answer isn't the user it's showing
//    (covers cookie expiry, or a change the broadcast missed).
//
// It only acts on signed-in pages (/dashboard..., /admin...) and on the
// login / home page — never on the candidate's interview page
// (/screening/:token) or the verify / reset-password pages.

const isSignedInPage = (path) => path.startsWith("/dashboard") || path.startsWith("/admin");
const isEntryPage = (path) => path === "/" || path === "/login";

// Minimum time between "who am I?" checks when switching tabs.
const RECHECK_MS = 5000;

const idOf = (user) => (user ? String(user.id || user._id || "") : "");

export default function useSessionSync() {
  const { user, initialized } = useSelector((state) => state.auth);

  // Latest values for the event handlers below, without re-subscribing.
  const userIdRef = useRef("");
  const initializedRef = useRef(false);
  userIdRef.current = idOf(user);
  initializedRef.current = initialized;

  // 1. React to login / logout in other tabs.
  useEffect(() => {
    return subscribeToSessionChanges((message) => {
      const path = window.location.pathname;
      const currentId = userIdRef.current;

      if (message.type === "login") {
        // Same person signed in again in another tab -> nothing to change.
        if (message.userId && message.userId === currentId) return;
        if (isSignedInPage(path) || isEntryPage(path)) {
          // "/" sends the new user to their own home (/admin or /dashboard).
          window.location.assign("/");
        }
      }

      if (message.type === "logout" && currentId && isSignedInPage(path)) {
        window.location.assign("/login");
      }
    });
  }, []);

  // 2. Re-check the session when the tab becomes visible again.
  useEffect(() => {
    let lastCheck = 0;
    let checking = false;

    const recheck = async () => {
      if (document.visibilityState !== "visible") return;
      if (!initializedRef.current || !userIdRef.current) return; // nobody signed in here
      if (!isSignedInPage(window.location.pathname)) return;
      if (checking || Date.now() - lastCheck < RECHECK_MS) return;

      checking = true;
      lastCheck = Date.now();
      try {
        const res = await api.get("/user/me");
        if (idOf(res.data.user) !== userIdRef.current) {
          window.location.assign("/"); // cookie now belongs to someone else
        }
      } catch (err) {
        if ([401, 403, 404].includes(err.response?.status)) {
          window.location.assign("/login"); // logged out / session expired / user removed
        }
        // Network errors: do nothing, try again next time.
      } finally {
        checking = false;
      }
    };

    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, []);
}
